import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { useRestaurant } from '@/contexts/RestaurantContext'
import { usePermissions } from '@/hooks/usePermissions'
import { useAuth } from '@/contexts/AuthContext'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  ShoppingBag,
  UtensilsCrossed,
  Clock,
  Wrench,
  MapPin,
  Settings,
  MonitorCog,
  LayoutGrid,
  UserCog,
  Ticket,
  Calendar,
  BarChart3,
  LayoutDashboard,
  ShieldCheck,
  Store,
  Megaphone,
  FileText,
  Rocket,
  CloudUpload,
  Sparkles,
  Palette,
  Printer,
  ReceiptEuro,
  CalendarCheck2,
  Stethoscope,
  Moon,
  Sun,
  TabletSmartphone,
  RefreshCw,
  Download,
  Loader2,
  Stamp,
  Link2,
} from 'lucide-react'
import { canPrint } from '@/features/orders/hooks/usePrintOrder'
import { isLiveUpdateSupported } from '@/lib/appUpdate'
import { useAppVersionStatus } from '@/hooks/useAppVersionStatus'

interface NavItem {
  to: string
  icon: React.ElementType
  labelKey: string
  superAdminOnly?: boolean
  /** Only in the Android tablet app. */
  tabletOnly?: boolean
}

const navSections: { labelKey: string; items: NavItem[] }[] = [
  { labelKey: 'sections.operations', items: [
    { to: '/orders', icon: ShoppingBag, labelKey: 'orders' },
    { to: '/reservations', icon: CalendarCheck2, labelKey: 'reservations' },
    { to: '/opening-times', icon: Clock, labelKey: 'openingTimes' },
    { to: '/setup', icon: TabletSmartphone, labelKey: 'tablet', tabletOnly: true },
  ] },
  { labelKey: 'sections.reports', items: [
    { to: '/dashboard', icon: LayoutDashboard, labelKey: 'dashboard', superAdminOnly: true },
    { to: '/tax-report', icon: ReceiptEuro, labelKey: 'taxReport' },
    { to: '/statistics', icon: BarChart3, labelKey: 'statistics', superAdminOnly: true },
  ] },
  { labelKey: 'sections.menu', items: [
    { to: '/menu', icon: UtensilsCrossed, labelKey: 'menu', superAdminOnly: true },
    { to: '/print-menu', icon: Printer, labelKey: 'printMenu', superAdminOnly: true },
    { to: '/ai-menu', icon: Sparkles, labelKey: 'aiMenu', superAdminOnly: true },
    { to: '/visual-menu', icon: Palette, labelKey: 'visualMenu', superAdminOnly: true },
  ] },
  { labelKey: 'sections.customers', items: [
    { to: '/settings/vouchers', icon: Ticket, labelKey: 'vouchers', superAdminOnly: true },
    { to: '/loyalty', icon: Stamp, labelKey: 'loyalty', superAdminOnly: true },
    { to: '/announcements', icon: Megaphone, labelKey: 'announcements', superAdminOnly: true },
    { to: '/landing-page', icon: FileText, labelKey: 'landingPage', superAdminOnly: true },
  ] },
  { labelKey: 'sections.setup', items: [
    { to: '/settings', icon: Settings, labelKey: 'settings', superAdminOnly: true },
    { to: '/pos-setup', icon: MonitorCog, labelKey: 'posSetup', superAdminOnly: true },
    { to: '/pos-staff', icon: UserCog, labelKey: 'posStaff', superAdminOnly: true },
    { to: '/pos-tables', icon: LayoutGrid, labelKey: 'posTables', superAdminOnly: true },
    { to: '/admin/opening-times-editor', icon: Wrench, labelKey: 'openingTimesEditor', superAdminOnly: true },
    { to: '/delivery-areas', icon: MapPin, labelKey: 'deliveryAreas', superAdminOnly: true },
    { to: '/onboarding', icon: Rocket, labelKey: 'onboarding', superAdminOnly: true },
  ] },
  { labelKey: 'sections.internal', items: [
    { to: '/restaurants', icon: Store, labelKey: 'restaurants', superAdminOnly: true },
    { to: '/settings/holidays', icon: Calendar, labelKey: 'holidays', superAdminOnly: true },
    { to: '/deployment', icon: CloudUpload, labelKey: 'deployment', superAdminOnly: true },
    { to: '/app-links', icon: Link2, labelKey: 'appLinks', superAdminOnly: true },
    { to: '/diagnostics', icon: Stethoscope, labelKey: 'diagnostics', superAdminOnly: true },
  ] },
]

const accountSecurityItem: NavItem = { to: '/account/security', icon: ShieldCheck, labelKey: 'accountSecurity' }

// Theme toggle component for sidebar footer
function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const { t } = useTranslation('navigation')
  const isDark = theme === 'pos-theme'

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'pos-theme')}
      className="flex min-h-11 items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {isDark ? <Sun className="h-[18px] w-[18px] flex-shrink-0" /> : <Moon className="h-[18px] w-[18px] flex-shrink-0" />}
      <span className="font-medium">{isDark ? t('theme.light') : t('theme.dark')}</span>
    </button>
  )
}

// Native apps (tablet, iPhone): version and the manual update check, styled like the theme toggle
function AppVersionButton() {
  const { status, updateReady, detail, action, version } = useAppVersionStatus()
  const ready = Boolean(status.storeUpdate) || updateReady
  const Icon = status.checking ? Loader2 : ready ? Download : RefreshCw

  return (
    <button
      type="button"
      disabled={status.checking}
      onClick={() => void action.run()}
      className={cn(
        'flex min-h-11 items-center gap-3 w-full px-3 py-2 rounded-lg text-sm text-start transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        ready ? 'text-primary hover:bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
      )}
    >
      <Icon className={cn('h-[18px] w-[18px] flex-shrink-0', status.checking && 'animate-spin')} />
      <span className="min-w-0">
        <span className="block font-medium">{action.label}</span>
        <span className="block truncate text-xs text-muted-foreground" aria-live="polite">
          {status.checking || ready || status.failed ? detail : version}
        </span>
      </span>
    </button>
  )
}

// Brand mark: rounded tile with the Leckerorder initial
function BrandMark() {
  return (
    <div className="h-9 w-9 shrink-0 rounded-xl bg-gradient-to-br from-brand-bright to-brand flex items-center justify-center shadow-sm">
      <span className="font-display font-bold text-[17px] leading-none text-white">L</span>
    </div>
  )
}

// Shared navigation content used by both sidebar and drawer
function NavigationContent({ onItemClick }: { onItemClick?: () => void }) {
  const { t } = useTranslation('common')
  const { t: tNav } = useTranslation('navigation')
  const location = useLocation()
  const { selectedRestaurant } = useRestaurant()
  const { isSuperAdmin, canManageHolidayTemplates, canManageRestaurants } = usePermissions()
  const { configuration, user } = useAuth()

  const isActive = (path: string) => location.pathname === path

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon
    const active = isActive(item.to)

    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onItemClick}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'relative flex min-h-11 items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active
            ? 'bg-primary/10 text-primary font-semibold'
            : 'text-muted-foreground font-medium hover:bg-accent/60 hover:text-foreground'
        )}
      >
        {/* Active accent bar (logical start side for RTL) */}
        <span
          className={cn(
            'absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-primary transition-opacity',
            active ? 'opacity-100' : 'opacity-0'
          )}
        />
        <Icon
          aria-hidden="true"
          className={cn(
            'h-[18px] w-[18px] flex-shrink-0 transition-colors',
            active ? 'text-primary' : 'text-muted-foreground/70 group-hover:text-foreground'
          )}
        />
        <span className="truncate">{tNav(item.labelKey)}</span>
      </Link>
    )
  }

  const visibleSections = navSections.map(section => ({ ...section, items: section.items.filter(item => {
    if (item.labelKey === 'holidays') return canManageHolidayTemplates
    if (item.labelKey === 'restaurants') return canManageRestaurants
    if (item.tabletOnly && !canPrint) return false
    if (item.superAdminOnly) return isSuperAdmin
    return true
  }) })).filter(section => section.items.length > 0)

  return (
    <>
      {/* Header */}
      <div className="px-4 py-4 border-b border-border/60">
        <div className="flex items-center gap-3 min-w-0">
          <BrandMark />
          <div className="min-w-0">
            <h2 className="font-display text-[17px] font-bold tracking-tight leading-tight truncate">
              {t('nav.brand')}
            </h2>
            {selectedRestaurant && (
              <p className="text-xs text-muted-foreground truncate leading-tight mt-0.5">
                {selectedRestaurant.name}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Nav items */}
      <ScrollArea className="flex-1">
        <nav aria-label={tNav('navigationLabel')} className="px-3 py-3 space-y-5">
          {visibleSections.map(section => (
            <section key={section.labelKey} aria-label={tNav(section.labelKey)}>
              {isSuperAdmin && (
                <h3 className="px-3 pb-1 text-xs font-semibold text-muted-foreground">
                  {tNav(section.labelKey)}
                </h3>
              )}
              <div className="space-y-0.5">{section.items.map(renderNavItem)}</div>
            </section>
          ))}
        </nav>
      </ScrollArea>

      {/* Personal account actions */}
      <div className="shrink-0 p-3 border-t border-border/60 space-y-0.5">
        {isSuperAdmin && user?.personalAccount && configuration.cognitoEnabled && renderNavItem(accountSecurityItem)}
        {isLiveUpdateSupported() && <AppVersionButton />}
        <ThemeToggle />
        {!isLiveUpdateSupported() && (
          <p className="px-3 pt-1 text-xs text-muted-foreground">{t('appVersion.title', { version: __APP_VERSION__ })}</p>
        )}
      </div>
    </>
  )
}

// Mobile drawer (slide-out)
interface NavigationDrawerProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

const EDGE_PX = 30
const SWIPE_PX = 50

export function NavigationDrawer({ isOpen, onOpen, onClose }: NavigationDrawerProps) {
  const closeSwipeStart = useRef<number | null>(null)

  // Swiping in from the left edge opens the menu, where the sidebar is hidden (below lg).
  useEffect(() => {
    if (isOpen) return
    let start: { x: number; y: number } | null = null
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      start = e.touches.length === 1 && touch.clientX < EDGE_PX && !window.matchMedia('(min-width: 1024px)').matches
        ? { x: touch.clientX, y: touch.clientY }
        : null
    }
    const onTouchMove = (e: TouchEvent) => {
      if (!start) return
      const touch = e.touches[0]
      if (Math.abs(touch.clientY - start.y) > 40) start = null
      else if (touch.clientX - start.x > SWIPE_PX) {
        start = null
        onOpen()
      }
    }
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
    }
  }, [isOpen, onOpen])

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="left"
        className="w-[280px] p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] flex flex-col"
        onTouchStart={(e) => { closeSwipeStart.current = e.touches[0].clientX }}
        onTouchMove={(e) => {
          if (closeSwipeStart.current !== null && closeSwipeStart.current - e.touches[0].clientX > SWIPE_PX) {
            closeSwipeStart.current = null
            onClose()
          }
        }}
      >
        <NavigationContent onItemClick={onClose} />
      </SheetContent>
    </Sheet>
  )
}

// Desktop persistent sidebar
export function NavigationSidebar({ className }: { className?: string }) {
  return (
    <aside
      className={cn(
        'w-[240px] border-e border-border/60 bg-card flex flex-col h-full pt-[env(safe-area-inset-top)]',
        className
      )}
    >
      <NavigationContent />
    </aside>
  )
}
